import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Section, SectionHeading, FadeIn } from "@/components/Section";
import { ArrowUpRight, Clock } from "lucide-react";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/blog/posts")
      .then((r) => setPosts(r.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div data-testid="blog-page">
      <Section className="pt-28 pb-10">
        <SectionHeading
          eyebrow="Notes from the operating layer"
          title="Infrastructure thinking for modern clinic operators."
          intro="Essays and frameworks on cash-pay programs, vendor coordination, contracted-rate economics, and the operational patterns behind sustainable clinic growth."
        />
      </Section>

      <Section className="pt-0">
        {loading && <div className="text-slate-500 text-sm">Loading posts…</div>}

        {featured && (
          <FadeIn>
            <Link
              to={`/blog/${featured.slug}`}
              data-testid="blog-featured-post"
              className="group grid lg:grid-cols-12 gap-8 rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300 transition-colors"
            >
              <div className="lg:col-span-6 aspect-[4/3] rounded-xl overflow-hidden bg-slate-100">
                <img src={featured.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
              </div>
              <div className="lg:col-span-6 flex flex-col justify-center">
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600">
                  <span>{featured.category}</span>
                  <span className="text-slate-300">•</span>
                  <span className="inline-flex items-center gap-1 text-slate-500 normal-case tracking-normal font-medium">
                    <Clock className="w-3 h-3" /> {featured.read_minutes} min read
                  </span>
                </div>
                <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
                  {featured.title}
                </h2>
                <p className="mt-3 text-slate-600 leading-relaxed">{featured.excerpt}</p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                  Read the essay <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </div>
            </Link>
          </FadeIn>
        )}

        {rest.length > 0 && (
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p, i) => (
              <FadeIn key={p.id} delay={i * 0.05}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-post-${p.slug}`}
                  className="group block h-full rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-slate-300 transition-colors"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <img src={p.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600">
                      <span>{p.category}</span>
                      <span className="text-slate-300">•</span>
                      <span className="inline-flex items-center gap-1 text-slate-500 normal-case tracking-normal font-medium">
                        <Clock className="w-3 h-3" /> {p.read_minutes} min
                      </span>
                    </div>
                    <h3 className="mt-2 font-display text-lg font-semibold text-slate-900 leading-snug">{p.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 line-clamp-3">{p.excerpt}</p>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
